import type {EquipmentItem} from './equipment';
import {abilityLabel, conditionLabel, damageTypeLabel} from './labels';
import type {FeatureProps} from './types';

const signed = (value: unknown) => {
	const number = Number(value) || 0;
	return number >= 0 ? `+${number}` : `−${-number}`;
};

const list = (values: unknown, label: (value: string) => string) =>
	(Array.isArray(values) ? values : []).map((value) => label(String(value))).join(', ');

function propsSummary(props: FeatureProps | undefined): string[] {
	if (!props) return [];
	return [
		props.acBonus ? `${signed(props.acBonus)} к КБ` : '',
		props.weapon ? `${signed(props.weapon.attackBonus)} к атаке, ${signed(props.weapon.damageBonus)} к урону` : '',
		props.unarmedStrike ? `${signed(props.unarmedStrike.attackBonus)} к безоружным ударам` : '',
		props.savingThrowBonus ? `${signed(props.savingThrowBonus)} к испытаниям` : '',
		props.abilityCheckBonus ? `${signed(props.abilityCheckBonus)} к проверкам` : '',
		props.spellAttackBonus ? `${signed(props.spellAttackBonus)} к атакам заклинаниями` : '',
		props.spellSaveDcBonus ? `${signed(props.spellSaveDcBonus)} к СЛ заклинаний` : '',
		...Object.entries(props.abilityMinimum || {}).map(
			([ability, value]) => `${abilityLabel(ability)} не ниже ${value}`,
		),
		...Object.entries(props.abilityIncrease || {})
			.filter(([ability]) => ability !== 'cap')
			.map(([ability, value]) => `${abilityLabel(ability)} ${signed(value)}`),
		props.resists ? `сопротивление: ${list(props.resists, damageTypeLabel)}` : '',
		props.immunities ? `невосприимчивость: ${list(props.immunities, damageTypeLabel)}` : '',
		props.conditionImmunities ? `невосприимчивость: ${list(props.conditionImmunities, conditionLabel)}` : '',
		props.resources?.some((resource) => resource.id === 'charges')
			? `заряды: ${props.resources.find((resource) => resource.id === 'charges')!.max}`
			: '',
		props.spells?.length ? `заклинания: ${props.spells.length}` : '',
	].filter(Boolean);
}

export type ItemEffectSummary = {
	equipped: string[];
	attuned: string[];
};

export const itemEffectSummary = (item: EquipmentItem): ItemEffectSummary => ({
	equipped: propsSummary(item.props.equipped),
	attuned: propsSummary(item.props.attuned),
});
